import type { FieldError, FieldErrors } from "react-hook-form";
import type {
  BudgetData,
  BudgetPhaseForm,
} from "../components/GuidedBudgetEditor/BudgetSelector";

type SegmentFieldErrors = FieldErrors<BudgetPhaseForm>;

/** First user-facing message anywhere on the budget form (footer, toast, global phased alert). */
export function flattenScheduledBudgetFormErrors(
  errors: FieldErrors<BudgetData> | undefined
): string | undefined {
  if (!errors) return undefined;

  const withRoot = errors as FieldErrors<BudgetData> & {
    root?: { message?: string };
  };
  if (typeof withRoot.root?.message === "string" && withRoot.root.message.trim()) {
    return withRoot.root.message.trim();
  }

  const visit = (node: unknown): string | undefined => {
    if (node == null) return undefined;
    if (typeof node !== "object") return undefined;
    const o = node as Record<string, unknown>;
    if (typeof o.message === "string" && o.message.trim()) return o.message;
    if (Array.isArray(o)) {
      for (const item of o) {
        const m = visit(item);
        if (m) return m;
      }
      return undefined;
    }
    for (const [key, v] of Object.entries(o)) {
      if (key === "ref" || key === "types") continue;
      const m = visit(v);
      if (m) return m;
    }
    return undefined;
  };
  return visit(errors);
}

export function scheduledBudgetSegmentFieldErrors(
  formErrors: FieldErrors<BudgetData> | undefined,
  index: number
): SegmentFieldErrors | undefined {
  const rows = formErrors?.segments;
  if (!Array.isArray(rows)) return undefined;
  return rows[index];
}

export function scheduledBudgetSegmentRowMessage(
  rowErrors: SegmentFieldErrors | undefined
): string | null {
  if (!rowErrors || typeof rowErrors !== "object") return null;
  const parts = [
    rowErrors.time_start?.message,
    rowErrors.time_end?.message,
    rowErrors.amount?.amount?.message,
  ]
    .map((m) => (typeof m === "string" ? m.trim() : ""))
    .filter(Boolean);
  return parts.length ? parts.join(" ") : null;
}

export function scheduledBudgetHasPerSegmentFieldErrors(
  formErrors: FieldErrors<BudgetData> | undefined
): boolean {
  const rows = formErrors?.segments;
  if (!Array.isArray(rows)) return false;
  return rows.some((row) => Boolean(scheduledBudgetSegmentRowMessage(row)));
}

export function scheduledBudgetSegmentInvalidTimeField(
  rowErrors: SegmentFieldErrors | undefined
): "start" | "end" | null {
  if (rowErrors?.time_start?.message?.trim()) return "start";
  if (rowErrors?.time_end?.message?.trim()) return "end";
  return null;
}

export function scheduledBudgetSegmentInvalidAmount(
  rowErrors: SegmentFieldErrors | undefined
): boolean {
  return Boolean(rowErrors?.amount?.amount?.message?.trim());
}

/** Whole-array error (e.g. delivery window) when Yup targets `segments` without an index. */
export function scheduledBudgetSegmentsArrayMessage(
  formErrors: FieldErrors<BudgetData> | undefined
): string | undefined {
  const segments = formErrors?.segments;
  if (!segments || Array.isArray(segments)) return undefined;
  const msg = (segments as FieldError).message;
  return typeof msg === "string" && msg.trim() ? msg.trim() : undefined;
}
